import React, { useState, useCallback } from 'react';
import { useIsMobile } from './hooks/useIsMobile';
import { SearchPanel } from './components/SearchPanel';
import { ResultsPanel } from './components/ResultsPanel';
import { TrackedProductsPanel } from './components/TrackedProductsPanel';
import { NotificationsPanel } from './components/NotificationsPanel';
import { CatalogPanel } from './components/CatalogPanel';
import { UpcomingPanel } from './components/UpcomingPanel';
import { PWAInstallBanner } from './components/PWAInstallBanner';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { searchPreorders } from './utils/api';
import { PreorderResult, SearchPreordersRequest, Tab } from './types';

const TABS: { id: Tab; label: string }[] = [
  { id: 'catalog', label: 'All Products' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'search', label: 'Search Stores' },
  { id: 'results', label: 'Results' },
  { id: 'tracked', label: 'Tracked' },
  { id: 'notifications', label: 'Notifications' },
];

export function App() {
  const [activeTab, setActiveTab] = useState<Tab>('catalog');
  const [results, setResults] = useState<PreorderResult[]>([]);
  const [resultMessage, setResultMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [trackedKey, setTrackedKey] = useState(0);
  const [lastSearch, setLastSearch] = useState<SearchPreordersRequest | undefined>();
  const isOnline = useOnlineStatus();
  const isMobile = useIsMobile();

  const handleSearch = useCallback(async (req: SearchPreordersRequest) => {
    setLoading(true);
    setLastSearch(req);
    setActiveTab('results');
    try {
      const res = await searchPreorders(req);
      setResults(res.results);
      setResultMessage(res.message);
    } catch (err) {
      setResults([]);
      setResultMessage(err instanceof Error ? err.message : 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  function handleProductTracked() {
    setTrackedKey((k) => k + 1);
  }

  async function handleLogout() {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    window.location.replace('/login');
  }

  return (
    <div style={styles.app}>
      <PWAInstallBanner />

      {!isOnline && (
        <div style={styles.offlineBanner}>
          You are offline — showing cached data where available.
        </div>
      )}

      <header style={styles.header}>
        <div style={{ ...styles.headerInner, padding: isMobile ? '12px 0' : '20px 0' }}>
          <div>
            <h1 style={{ ...styles.title, fontSize: isMobile ? '18px' : '22px' }}>DB Preorder Scout</h1>
            {!isMobile && <p style={styles.subtitle}>Fusion World &amp; Masters preorder finder</p>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={styles.onlineDot} title={isOnline ? 'Online' : 'Offline'}>
              <span style={{ ...styles.dot, background: isOnline ? '#22c55e' : '#ef4444' }} />
              <span style={{ fontSize: '12px', color: '#8a93b0' }}>{isOnline ? 'Live' : 'Offline'}</span>
            </div>
            <button style={styles.logoutBtn} onClick={handleLogout} title="Sign out">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <nav style={styles.nav} className="scroll-x">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.navBtn,
              ...(activeTab === tab.id ? styles.navBtnActive : {}),
              ...(isMobile ? { padding: '12px 14px', fontSize: '13px' } : {}),
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.id === 'results' && results.length > 0 && (
              <span style={styles.badge}>{results.length}</span>
            )}
          </button>
        ))}
      </nav>

      <main style={{
        ...styles.main,
        padding: isMobile ? '16px 12px' : '24px',
        // Catalog needs the full canvas so cards can fill horizontally
        maxWidth: activeTab === 'catalog' ? '1600px' : '1200px',
      }}>
        {activeTab === 'catalog' && (
          <section style={styles.panel}>
            <h2 style={styles.panelTitle}>All Products</h2>
            <p style={{ color: '#8a93b0', fontSize: '13px', marginTop: '-12px' }}>
              Complete Fusion World &amp; Masters release timeline — click any card for details and store links.
            </p>
            <CatalogPanel />
          </section>
        )}

        {activeTab === 'upcoming' && (
          <section style={styles.panel}>
            <h2 style={styles.panelTitle}>Upcoming Releases</h2>
            <p style={{ color: '#8a93b0', fontSize: '13px', marginTop: '-12px' }}>
              All future Fusion World &amp; Masters releases — search stores or track any product directly from here.
            </p>
            <UpcomingPanel onSearch={handleSearch} />
          </section>
        )}

        {activeTab === 'search' && (
          <section style={styles.panel}>
            <h2 style={styles.panelTitle}>Search Preorders</h2>
            <p style={{ color: '#8a93b0', fontSize: '13px', marginTop: '-12px' }}>
              Pick a product line, select your regions, and hit Search — or jump here from the Upcoming tab to auto-fill.
            </p>
            <SearchPanel onSearch={handleSearch} loading={loading} initialSearch={lastSearch} />
          </section>
        )}

        {activeTab === 'results' && (
          <section style={styles.panel}>
            <h2 style={styles.panelTitle}>Results</h2>
            <ResultsPanel
              results={results}
              message={resultMessage}
              loading={loading}
              onProductTracked={handleProductTracked}
            />
          </section>
        )}

        {activeTab === 'tracked' && (
          <section style={styles.panel}>
            <h2 style={styles.panelTitle}>Tracked Products</h2>
            <TrackedProductsPanel key={trackedKey} />
          </section>
        )}

        {activeTab === 'notifications' && (
          <section style={styles.panel}>
            <h2 style={styles.panelTitle}>Notification Settings</h2>
            <NotificationsPanel />
          </section>
        )}
      </main>

      <footer style={styles.footer}>
        DB Preorder Scout · Data for informational purposes only · Always verify on official store pages
      </footer>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  app: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  offlineBanner: {
    background: '#ef444420', borderBottom: '1px solid #ef4444',
    color: '#ef4444', padding: '8px 20px', fontSize: '13px', textAlign: 'center',
  },
  header: { background: '#1a1d27', borderBottom: '1.5px solid #2e3347', padding: '0 24px' },
  headerInner: {
    maxWidth: '1200px', margin: '0 auto', padding: '20px 0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  title: { fontSize: '22px', fontWeight: 800, color: '#f97316', letterSpacing: '-0.02em' },
  subtitle: { fontSize: '13px', color: '#8a93b0', marginTop: '2px' },
  onlineDot: { display: 'flex', alignItems: 'center', gap: '6px' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block' },
  nav: {
    background: '#1a1d27', borderBottom: '1.5px solid #2e3347',
    display: 'flex', gap: '4px', padding: '0 24px', overflowX: 'auto' as const,
  },
  navBtn: {
    padding: '14px 18px', background: 'transparent', border: 'none',
    color: '#8a93b0', cursor: 'pointer', fontSize: '14px', fontWeight: 500,
    borderBottom: '2.5px solid transparent', display: 'flex', alignItems: 'center', gap: '6px',
    whiteSpace: 'nowrap' as const, transition: 'color 0.15s',
  },
  navBtnActive: { color: '#f97316', borderBottomColor: '#f97316' },
  badge: {
    background: '#f97316', color: '#fff', fontSize: '11px',
    fontWeight: 700, padding: '1px 6px', borderRadius: '10px',
  },
  main: { flex: 1, padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%' },
  panel: { display: 'flex', flexDirection: 'column', gap: '24px' },
  panelTitle: { fontSize: '20px', fontWeight: 700 },
  logoutBtn: {
    background: 'transparent', border: '1px solid #2e3347', borderRadius: '6px',
    color: '#8a93b0', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
    padding: '5px 10px', transition: 'border-color 0.15s, color 0.15s',
  },
  footer: {
    background: '#1a1d27', borderTop: '1.5px solid #2e3347',
    padding: '14px 24px', textAlign: 'center', fontSize: '12px', color: '#8a93b0',
  },
};
